/**
 * Boot Scene
 *
 * Loads critical assets and initializes services before starting the game.
 */

import Phaser from 'phaser';
import { CollectibleTextureGenerator } from '@/rendering/CollectibleTextureGenerator';
import { TYPOGRAPHY, Z_INDEX } from '@/config/designSystem';
import { MenuParticleSystem } from '@/ui/effects/MenuParticleSystem';
import { MenuBlobo } from '@/ui/components/MenuBlobo';

export class BootScene extends Phaser.Scene {
  constructor() {
    super({ key: 'BootScene' });
  }

  private progressText?: Phaser.GameObjects.Text;
  private loadingText?: Phaser.GameObjects.Text;
  private blobo?: MenuBlobo;
  private tiltAngle: number = 0;
  private failedBackgrounds: Set<string> = new Set();
  private loadedBackgrounds: Set<string> = new Set();
  private readonly BACKGROUND_KEYS = ['bg-far-full', 'bg-mid-full', 'bg-near-full'];

  preload(): void {
    // Create loading bar
    this.createLoadingBar();

    // Create procedural graphics for Blobo and platforms
    this.createBloboTexture();
    this.createPlatformTexture();
    this.createParticleTexture();

    // Preload collectible sprite assets (PNG files from public/sprites/collectibles/)
    // Falls back to procedural generation for missing sprites
    CollectibleTextureGenerator.preloadSpriteAssets(this);

    // Load Material Icon SVG files
    // These are real Google Material Symbols icons used throughout the UI
    this.load.svg('icon-trophy', 'icons/material/trophy.svg');
    this.load.svg('icon-settings', 'icons/material/settings.svg');
    this.load.svg('icon-install', 'icons/material/install.svg');
    this.load.svg('icon-edit', 'icons/material/edit.svg');
    this.load.svg('icon-pause', 'icons/material/pause.svg');
    this.load.svg('icon-play-circle', 'icons/material/play_circle.svg');
    this.load.svg('icon-target', 'icons/material/target.svg');
    this.load.svg('icon-statistics', 'icons/material/statistics.svg');
    this.load.svg('icon-timer', 'icons/material/timer.svg');
    this.load.svg('icon-height', 'icons/material/height.svg');
    this.load.svg('icon-share', 'icons/material/share.svg');
    this.load.svg('icon-refresh', 'icons/material/refresh.svg');
    this.load.svg('icon-wifi-off', 'icons/material/wifi-off.svg');
    this.load.svg('icon-star', 'icons/material/star.svg');
    this.load.svg('icon-medal', 'icons/material/medal.svg');
    this.load.svg('icon-medal-gold', 'icons/material/medal-gold.svg');
    this.load.svg('icon-medal-silver', 'icons/material/medal-silver.svg');
    this.load.svg('icon-medal-bronze', 'icons/material/medal-bronze.svg');
    this.load.svg('icon-play-circle-black', 'icons/material/play_circle_black.svg');
    this.load.svg('icon-trophy-gold', 'icons/material/trophy-gold.svg');
    this.load.svg('icon-diamond', 'icons/material/diamond.svg');
    this.load.svg('icon-coin', 'icons/material/coin.svg');
    this.load.svg('icon-arrow-back', 'icons/material/arrow-back.svg');
    this.load.svg('icon-explore', 'icons/material/explore.svg');
    this.load.svg('icon-info', 'icons/material/info.svg');
    this.load.svg('icon-close', 'icons/material/close.svg');

    // Load full-world background images (will be auto-split for mobile compatibility)
    // Android devices typically limit textures to 4096×4096
    // These will be automatically sliced into smaller segments at runtime
    this.load.image('bg-far-full', 'bg/background_far.png'); // 800×4534px
    this.load.image('bg-mid-full', 'bg/background_mid.png'); // 800×8224px
    this.load.image('bg-near-full', 'bg/background_near.png'); // 800×11299px

    // Load audio assets (Phase 5: Victory Zone)
    this.load.audio('victory-sound', 'victory_sound.mp3');

    // Progress text will be created in createLoadingBar()
    // (positioned below loading text)

    // Track successful background image loads
    this.load.on('filecomplete-image-bg-far-full', () => {
      console.log('✅ Loaded: bg-far-full (background_far.png)');
      this.loadedBackgrounds.add('bg-far-full');
    });
    this.load.on('filecomplete-image-bg-mid-full', () => {
      console.log('✅ Loaded: bg-mid-full (background_mid.png)');
      this.loadedBackgrounds.add('bg-mid-full');
    });
    this.load.on('filecomplete-image-bg-near-full', () => {
      console.log('✅ Loaded: bg-near-full (background_near.png)');
      this.loadedBackgrounds.add('bg-near-full');
    });

    // Track failed loads and retry
    this.load.on('loaderror', (file: any) => {
      // Only handle background images
      if (this.BACKGROUND_KEYS.includes(file.key)) {
        console.error(`❌ Failed to load ${file.key} from ${file.url}`);
        this.failedBackgrounds.add(file.key);

        // Retry once after a short delay
        this.time.delayedCall(500, () => {
          if (this.failedBackgrounds.has(file.key) && !this.loadedBackgrounds.has(file.key)) {
            console.log(`🔄 Retrying load: ${file.key}`);
            this.load.image(file.key, file.url);
            this.load.start(); // Manually start the loader for retry
          }
        });
      }
    });

    // Update progress text (instead of creating new text objects)
    this.load.on('progress', (value: number) => {
      if (this.progressText) {
        this.progressText.setText(`${Math.round(value * 100)}%`);
      }
    });
  }

  async create(): Promise<void> {
    // Check device capabilities first
    this.checkDeviceCapabilities();

    // Auto-split background images for mobile compatibility
    console.log('🔪 Auto-splitting background images for mobile compatibility...');
    this.splitBackgroundImages();

    // Convert black SVG icons to white textures (so tinting works properly)
    console.log('🎨 Converting Material Icon SVGs to white textures...');
    this.convertSVGsToWhiteTextures();

    // Request device orientation permissions (iOS 13+)
    this.requestOrientationPermission();

    // Wait for web fonts to load (Josefin Sans only - no longer using Material Symbols font)
    try {
      console.log('⏳ Waiting for fonts to load...');
      await document.fonts.ready;
      console.log('✅ Fonts loaded successfully');
    } catch (error) {
      console.warn('⚠️ Font loading check failed, continuing anyway:', error);
    }

    // Always go to main menu - it will handle showing install button if needed
    console.log('🎮 Starting main menu');
    this.scene.start('MainMenuScene');
  }

  private createLoadingBar(): void {
    const centerX = this.cameras.main.centerX;
    const centerY = this.cameras.main.centerY;
    const height = this.cameras.main.height;

    // Dark background matching main menu
    this.cameras.main.setBackgroundColor('#0A0E27');

    // Create floating particles for atmosphere (listens to tilt-update events automatically)
    new MenuParticleSystem(this);

    // Set up tilt system to feed events to particles and Blobo
    this.setupTiltSystem();

    // Create animated Blobo character at same position as main menu
    const bloboY = Math.min(centerY - 120, height * 0.35);
    this.blobo = new MenuBlobo(this, centerX, bloboY, 80);
    this.blobo.setDepth(Z_INDEX.uiContent);

    // Loading text below Blobo
    this.loadingText = this.add.text(centerX, bloboY + 160, 'LOADING...', {
      fontSize: '32px',
      color: '#ffffff',
      fontFamily: TYPOGRAPHY.fonts.josefin,
      resolution: 2, // Fixed resolution for consistent crisp rendering
    });
    this.loadingText.setOrigin(0.5);
    this.loadingText.setDepth(Z_INDEX.uiContent);

    // Progress percentage below loading text
    this.progressText = this.add.text(centerX, bloboY + 210, '0%', {
      fontSize: '24px',
      color: '#ffffff',
      fontFamily: TYPOGRAPHY.fonts.josefin,
      resolution: 2, // Fixed resolution for consistent crisp rendering
    });
    this.progressText.setOrigin(0.5);
    this.progressText.setDepth(Z_INDEX.uiContent);
  }

  private createBloboTexture(): void {
    // Create a white circle texture for Blobo
    const graphics = this.add.graphics();
    graphics.fillStyle(0xffffff, 1);
    graphics.fillCircle(50, 50, 50); // Center at 50,50 with radius 50

    // Add a subtle gradient effect
    graphics.lineStyle(2, 0xcccccc, 1);
    graphics.strokeCircle(50, 50, 50);

    graphics.generateTexture('blobo', 100, 100);
    graphics.destroy();
  }

  private createPlatformTexture(): void {
    // Create a simple rectangle texture for platforms
    const graphics = this.add.graphics();
    graphics.fillStyle(0x808080, 1);
    graphics.fillRect(0, 0, 200, 20);

    // Add border
    graphics.lineStyle(2, 0x666666, 1);
    graphics.strokeRect(0, 0, 200, 20);

    graphics.generateTexture('platform', 200, 20);
    graphics.destroy();
  }

  private createParticleTexture(): void {
    // Create a small circle for particles
    const graphics = this.add.graphics();
    graphics.fillStyle(0xffffff, 1);
    graphics.fillCircle(4, 4, 4);

    graphics.generateTexture('particle', 8, 8);
    graphics.destroy();
  }

  private splitBackgroundImages(): void {
    // Split large background images into mobile-friendly segments (≤4000px each)
    const layers = [
      { key: 'bg-far', fullKey: 'bg-far-full', height: 4534, numSegments: 2 },
      { key: 'bg-mid', fullKey: 'bg-mid-full', height: 8224, numSegments: 3 },
      { key: 'bg-near', fullKey: 'bg-near-full', height: 11299, numSegments: 3 },
    ];

    layers.forEach(layer => {
      // Get the full-size texture
      const texture = this.textures.get(layer.fullKey);
      if (!texture || texture.key === '__MISSING') {
        console.error(`❌ Failed to load ${layer.fullKey} - segments will not be created`);
        return;
      }

      const source = texture.getSourceImage() as HTMLImageElement;

      // Verify image actually loaded (check naturalWidth)
      if (!source.complete || source.naturalWidth === 0) {
        console.error(
          `❌ ${layer.fullKey} didn't load properly (incomplete or 0px width) - segments will not be created`
        );
        this.textures.remove(layer.fullKey);
        return;
      }

      const width = source.width;
      const height = source.height;

      // Calculate segment height (distribute evenly)
      const segmentHeight = Math.ceil(height / layer.numSegments);

      console.log(
        `  📐 ${layer.key}: ${width}×${height} → ${layer.numSegments} segments of ~${segmentHeight}px`
      );

      // Create segments from top to bottom
      for (let i = 0; i < layer.numSegments; i++) {
        const startY = i * segmentHeight;
        const actualHeight = Math.min(segmentHeight, height - startY);

        // Create canvas for this segment
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = actualHeight;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          console.error('Failed to get canvas context');
          continue;
        }

        // Draw the segment from source image
        ctx.drawImage(
          source,
          0,
          startY,
          width,
          actualHeight, // Source rectangle (sx, sy, sw, sh)
          0,
          0,
          width,
          actualHeight // Destination rectangle (dx, dy, dw, dh)
        );

        // Generate Phaser texture from canvas
        const segmentKey = `${layer.key}-${i + 1}`;
        this.textures.addCanvas(segmentKey, canvas);

        console.log(`    ✓ Created ${segmentKey}: ${width}×${actualHeight}px`);
      }

      // Clean up the full-size texture to save memory
      this.textures.remove(layer.fullKey);
    });

    console.log('✅ All backgrounds auto-split successfully!');
  }

  private checkDeviceCapabilities(): void {
    // Check for gyroscope support
    const hasGyroscope = 'DeviceOrientationEvent' in window;

    if (!hasGyroscope) {
      console.warn('Device does not support gyroscope. Tilt controls will be disabled.');
    }

    // Check for touch support
    const hasTouch = 'ontouchstart' in window;

    if (!hasTouch) {
      console.warn('Touch not detected. Using mouse input.');
    }

    // Check maximum texture size (critical for background images)
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    let maxTextureSize = 2048; // Conservative default

    if (gl) {
      maxTextureSize = (gl as WebGLRenderingContext).getParameter(
        (gl as WebGLRenderingContext).MAX_TEXTURE_SIZE
      );
      console.log(`📐 Device max texture size: ${maxTextureSize}×${maxTextureSize}`);
    } else {
      console.warn('⚠️ Could not detect max texture size, assuming 2048×2048');
    }

    // Validate that auto-split segments will fit within device limits
    // Segments are created as ~4000px max (far: 2267×2, mid: 2742+2741×2, near: 3767+3766×2)
    const maxSegmentHeight = 4000;

    if (maxSegmentHeight > maxTextureSize) {
      console.error(
        `🚨 Background segments (${maxSegmentHeight}px) may EXCEED device limit (${maxTextureSize})!`
      );
      console.warn(`   Consider reducing segment size in splitBackgroundImages()`);
    } else {
      console.log(
        `✅ Background segments (≤${maxSegmentHeight}px) fit within device limits (${maxTextureSize}×${maxTextureSize})`
      );
    }

    // Store capabilities in registry for other scenes
    this.registry.set('hasGyroscope', hasGyroscope);
    this.registry.set('hasTouch', hasTouch);
    this.registry.set('maxTextureSize', maxTextureSize);
  }

  /**
   * Update loop - handle tilt parallax effects
   */
  update(): void {
    // Particles automatically respond to 'tilt-update' events via their internal listener

    // Blobo tilts slightly with device orientation
    if (this.blobo) {
      const bloboTilt = this.tiltAngle * 0.3; // Less sensitive than particles
      this.blobo.setRotation(Phaser.Math.DegToRad(bloboTilt));
    }
  }

  /**
   * Set up tilt system for parallax effects
   */
  private setupTiltSystem(): void {
    // Listen for device orientation
    if (window.DeviceOrientationEvent) {
      window.addEventListener('deviceorientation', this.handleDeviceOrientation.bind(this));
    }

    // Keyboard simulation for testing
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        this.tiltAngle = Math.max(this.tiltAngle - 2, -15);
        this.emitTiltUpdate();
      } else if (event.key === 'ArrowRight') {
        this.tiltAngle = Math.min(this.tiltAngle + 2, 15);
        this.emitTiltUpdate();
      }
    });
  }

  /**
   * Handle device orientation events
   */
  private handleDeviceOrientation(event: DeviceOrientationEvent): void {
    if (event.gamma !== null) {
      // Use gamma (left/right tilt)
      // Clamp to ±15 degrees for subtle effect
      this.tiltAngle = Phaser.Math.Clamp(event.gamma, -15, 15);
      this.emitTiltUpdate();
    }
  }

  /**
   * Emit tilt update event for particles
   */
  private emitTiltUpdate(): void {
    this.events.emit('tilt-update', this.tiltAngle);
  }

  /**
   * SVG icons have fill="white" at the root <svg> element.
   * They load as white textures and can be tinted with setTintFill().
   */
  private convertSVGsToWhiteTextures(): void {
    console.log('✅ SVG icons load as white (fill="white" in source files)');
  }

  private async requestOrientationPermission(): Promise<void> {
    // iOS 13+ requires explicit permission for DeviceOrientation
    if (
      typeof DeviceOrientationEvent !== 'undefined' &&
      typeof (DeviceOrientationEvent as any).requestPermission === 'function'
    ) {
      try {
        // This will be triggered by user interaction in main menu
        console.log('DeviceOrientation permission required (iOS 13+)');
        this.registry.set('needsOrientationPermission', true);
      } catch (error) {
        console.error('DeviceOrientation permission error:', error);
      }
    } else {
      this.registry.set('needsOrientationPermission', false);
    }
  }

  /**
   * Cleanup on scene shutdown
   */
  shutdown(): void {
    // Remove device orientation listener
    if (window.DeviceOrientationEvent) {
      window.removeEventListener('deviceorientation', this.handleDeviceOrientation.bind(this));
    }
  }
}
